import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { useParams, useSearchParams } from "react-router-dom";
import { Providers } from "@chotca/app-shell";
import { NextIntlClientProvider } from "next-intl";
import { LOCALE, MESSAGES } from "@/boot/i18n";
import { AuthGate } from "@/components/AuthGate";
import { AdminAppShell } from "@/components/AdminAppShell";
import { DashboardClient } from "@/routes/dashboard/dashboard-client";
import { InvitesClient } from "@/routes/invites/invites-client";
import { AccountsClient } from "@/routes/accounts/accounts-client";
import { FinanceClient } from "@/routes/finance/finance-client";
import { OrgsClient } from "@/routes/orgs/orgs-client";
import { PostingsClient } from "@/routes/postings/postings-client";
import { PostingTemplatesClient } from "@/routes/posting-templates/posting-templates-client";
import { WorkersListClient } from "@/routes/kyc/workers/workers-list-client";
import { OrgsListClient } from "@/routes/kyc/orgs/orgs-list-client";
import { IdentityListClient } from "@/routes/kyc/identity/identity-list-client";
import { AbuseClient } from "@/routes/abuse/abuse-client";
import { CancellationsClient } from "@/routes/cancellations/cancellations-client";
import { ActAsClient } from "@/routes/orgs/[id]/act-as-client";
import { ActivityClient } from "@/routes/activity/activity-client";
import { ClaimInvitationsClient } from "@/routes/claim-invitations/claim-invitations-client";
import { SettingsClient } from "@/routes/settings/settings-client";
import { NotificationsClient } from "@/routes/notifications/notifications-client";
import { TodoClient } from "@/routes/todo/todo-client";
import { ShadowOrgsClient } from "@/routes/shadow-orgs/shadow-orgs-client";
import { WorkerDetailClient as KycWorkerDetail } from "@/routes/kyc/workers/[id]/worker-detail-client";
import { OrgDetailClient as KycOrgDetail } from "@/routes/kyc/orgs/[id]/org-detail-client";
import { IdentityDetailClient as KycIdentityDetail } from "@/routes/kyc/identity/[id]/identity-detail-client";
import { OrgDetailClient } from "@/routes/orgs/[id]/org-detail-client";
import { AccountDetailClient } from "@/routes/accounts/[id]/account-detail-client";
import { PostingDetailClient } from "@/routes/postings/[id]/posting-detail-client";
import { EditPostingClient } from "@/routes/postings/[id]/edit/edit-posting-client";
import { NewPostingClient } from "@/routes/postings/new/new-posting-client";
import { PostingsImportClient } from "@/routes/postings/import/import-client";
import { AdminLoginPage } from "@/routes/auth/login-page";

function RootShell() {
  return (
    <NextIntlClientProvider locale={LOCALE} messages={MESSAGES}>
      <Providers>
        <Outlet />
      </Providers>
    </NextIntlClientProvider>
  );
}

function AdminArea() {
  return (
    <AuthGate>
      {() => (
        <AdminAppShell brand="Admin">
          <Outlet />
        </AdminAppShell>
      )}
    </AuthGate>
  );
}

function KycWorkerDetailRoute() {
  const { id } = useParams<{ id: string }>();
  return <KycWorkerDetail id={id!} />;
}
function KycIdentityDetailRoute() {
  const { id } = useParams<{ id: string }>();
  return <KycIdentityDetail id={id!} />;
}
function KycOrgDetailRoute() {
  const { id } = useParams<{ id: string }>();
  return <KycOrgDetail id={id!} />;
}
function ActAsRoute() {
  const { id } = useParams<{ id: string }>();
  return <ActAsClient orgId={id!} />;
}
function OrgDetailRoute() {
  const { id } = useParams<{ id: string }>();
  return <OrgDetailClient orgId={id!} />;
}
function AccountDetailRoute() {
  const { id } = useParams<{ id: string }>();
  return <AccountDetailClient userId={id!} />;
}
function PostingDetailRoute() {
  const { id } = useParams<{ id: string }>();
  return <PostingDetailClient postingId={id!} />;
}
function EditPostingRoute() {
  const { id } = useParams<{ id: string }>();
  return <EditPostingClient postingId={id!} />;
}
function NewPostingRoute() {
  const [sp] = useSearchParams();
  return (
    <NewPostingClient
      initialOrgId={sp.get("orgId")}
      duplicateId={sp.get("duplicate")}
    />
  );
}

export const router = createBrowserRouter([
  {
    element: <RootShell />,
    children: [
      { path: "/", element: <Navigate to="/todo" replace /> },
      { path: "/login", element: <AdminLoginPage /> },
      {
        element: <AdminArea />,
        children: [
          { path: "/dashboard", element: <DashboardClient /> },
          { path: "/invites", element: <InvitesClient /> },
          { path: "/todo", element: <TodoClient /> },
          { path: "/shadow-orgs", element: <ShadowOrgsClient /> },
          { path: "/accounts", element: <AccountsClient /> },
          { path: "/accounts/:id", element: <AccountDetailRoute /> },
          { path: "/finance", element: <FinanceClient /> },
          { path: "/orgs", element: <OrgsClient /> },
          { path: "/orgs/:id", element: <OrgDetailRoute /> },
          { path: "/orgs/:id/act-as", element: <ActAsRoute /> },
          { path: "/postings", element: <PostingsClient /> },
          {
            path: "/posting-templates",
            element: <PostingTemplatesClient />,
          },
          { path: "/postings/new", element: <NewPostingRoute /> },
          { path: "/postings/import", element: <PostingsImportClient /> },
          { path: "/postings/:id", element: <PostingDetailRoute /> },
          { path: "/postings/:id/edit", element: <EditPostingRoute /> },
          { path: "/kyc/identity", element: <IdentityListClient /> },
          { path: "/kyc/identity/:id", element: <KycIdentityDetailRoute /> },
          { path: "/kyc/workers", element: <WorkersListClient /> },
          { path: "/kyc/workers/:id", element: <KycWorkerDetailRoute /> },
          { path: "/kyc/orgs", element: <OrgsListClient /> },
          { path: "/kyc/orgs/:id", element: <KycOrgDetailRoute /> },
          { path: "/abuse", element: <AbuseClient /> },
          { path: "/cancellations", element: <CancellationsClient /> },
          { path: "/activity", element: <ActivityClient /> },
          { path: "/claim-invitations", element: <ClaimInvitationsClient /> },
          { path: "/notifications", element: <NotificationsClient /> },
          { path: "/settings", element: <SettingsClient /> },
        ],
      },
    ],
  },
]);
